import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { getBackendUri } from "@/lib/backendSource";
import { PERSON_EXISTS_QUERY } from "./queries";

export type SyncPersonFromCcmsResult =
  | { status: "found"; pgaId: string }
  | { status: "not_in_ccms"; pgaId: string }
  | { status: "timeout"; pgaId: string }
  | { status: "error"; message: string };

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 45000;

async function personExists(
  client: ApolloClient<NormalizedCacheObject> | ApolloClient<object>,
  pgaId: string
): Promise<boolean> {
  try {
    const { data } = await client.query({
      query: PERSON_EXISTS_QUERY,
      variables: { pgaId },
      fetchPolicy: "network-only",
      errorPolicy: "ignore"
    });
    return Boolean(data?.person?.pgaId);
  } catch {
    return false;
  }
}

async function waitForPerson(
  client: ApolloClient<NormalizedCacheObject> | ApolloClient<object>,
  pgaId: string,
  signal?: AbortSignal
): Promise<"found" | "timeout"> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    if (signal?.aborted) {
      return "timeout";
    }

    if (await personExists(client, pgaId)) {
      return "found";
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }

  return (await personExists(client, pgaId)) ? "found" : "timeout";
}

function extractErrorText(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  const value = record.errors ?? record.error ?? record.message;
  if (typeof value === "string" && value.trim()) return value.trim();
  if (Array.isArray(value) && value.length > 0) return value.map(String).join(", ");
  return null;
}

function isNotFoundInCcmsResponse(status: number, payload: unknown): boolean {
  if (status === 404) return true;
  if (!payload || typeof payload !== "object") return false;

  const record = payload as Record<string, unknown>;
  return record.code === "not_found_in_ccms";
}

/**
 * Ensures a person is available in M360 for the given PGA ID.
 * Checks GraphQL first, then triggers ServiceBus member sync and polls until the person appears.
 */
export async function syncPersonFromCcms(options: {
  pgaId: string;
  getAccessToken: () => Promise<string | undefined>;
  apolloClient: ApolloClient<NormalizedCacheObject> | ApolloClient<object>;
  signal?: AbortSignal;
}): Promise<SyncPersonFromCcmsResult> {
  const { pgaId, getAccessToken, apolloClient, signal } = options;

  if (await personExists(apolloClient, pgaId)) {
    return { status: "found", pgaId };
  }

  const backendUri = getBackendUri();
  if (!backendUri) {
    return { status: "error", message: "Backend URI not configured" };
  }

  const authToken = await getAccessToken();
  if (!authToken) {
    return { status: "error", message: "Authentication required. Please log in." };
  }

  let response: Response;
  try {
    response = await fetch(`${backendUri}/api/servicebus/member-lookup/${pgaId}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json"
      },
      signal
    });
  } catch (err) {
    if (signal?.aborted) {
      return { status: "error", message: "Lookup cancelled." };
    }
    return {
      status: "error",
      message: err instanceof Error ? err.message : "Failed to contact ServiceBus. Please try again."
    };
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (isNotFoundInCcmsResponse(response.status, payload)) {
    return { status: "not_in_ccms", pgaId };
  }

  if (!response.ok) {
    const detail = extractErrorText(payload);
    return {
      status: "error",
      message: detail ? `Member sync failed: ${detail}` : "Member sync failed. Please try again."
    };
  }

  const waitResult = await waitForPerson(apolloClient, pgaId, signal);
  if (waitResult === "found") {
    return { status: "found", pgaId };
  }

  return { status: "timeout", pgaId };
}
